<template>
    <row>
        <column cols="12" md="7" lg="8">
            <card xs-tile sm-tile>
                <card-title>{{ $t('label.event_acquisition_campaigns') }}</card-title>
                <card-subtitle >{{ $t('text.event.acquisition_campaigns.index') }}</card-subtitle>
                <card-text>
                    <fetch-loader v-if="pending || error" :pending="pending" :error="error" @retry="refresh"/>
					<missing-info v-else-if="!selectableCampaigns.length"/>
					<template v-else>
						<form-select v-if="selectableCampaigns.length > 1" :options="selectableCampaigns" v-model="selectedCampaignKey" :label="$t('label.campaign')" value-prop="_key"
									 text-prop="name" class="mt-8 sm:mt-0"/>
						<template v-if="selectedCampaign">
							<p v-if="selectableCampaigns.length === 1" class="subheading">{{ selectedCampaign.name }}</p>

							<div v-if="selectedCampaign.description" class="mb-16 whitespace-pre-wrap text-subtext-color">{{ selectedCampaign.description }}</div>

							<description-list small class="mb-16">
								<description-list-item :title="$t('label.discount')">
									<template #text>
										<div class="flex items-center">
											<div v-if="selectedCampaign.discount_amount" class="flex items-center">
												<span>{{ toEuro(selectedCampaign.discount_amount) }}</span>
												<tooltip left :text="$t('help.event.acquisition_campaign.discount', {discount: toEuro(selectedCampaign.discount_amount)})" with-touch>
													<icon :icon="icons.help" x-small color="gray-400" class="ml-4"/>
												</tooltip>
											</div>
											<div v-else-if="selectedCampaign.discount_percentage" class="flex items-center">
												<span>{{ selectedCampaign.discount_percentage }}%</span>
												<tooltip left :text="$t('help.event.acquisition_campaign.discount', {discount: `${selectedCampaign.discount_percentage}%`})" with-touch>
													<icon :icon="icons.help" x-small color="gray-400" class="ml-4"/>
												</tooltip>
											</div>
											<span v-else>-</span>
										</div>
									</template>
								</description-list-item>
								<description-list-item :title="$t('label.provision')">
									<template #text>
										<div class="flex items-center">
											<div v-if="selectedCampaign.provision_amount" class="flex items-center">
												<span>{{ toEuro(selectedCampaign.provision_amount) }}</span>
												<tooltip left :text="$t('help.event.acquisition_campaign.provision', {provision: toEuro(selectedCampaign.provision_amount)})" with-touch>
													<icon :icon="icons.help" x-small color="gray-400" class="ml-4"/>
												</tooltip>
											</div>
											<div v-else-if="selectedCampaign.provision_percentage" class="flex items-center">
												<span>{{ selectedCampaign.provision_percentage }}%</span>
												<tooltip left :text="$t('help.event.acquisition_campaign.provision', {provision: `${selectedCampaign.provision_percentage}%`})" with-touch>
													<icon :icon="icons.help" x-small color="gray-400" class="ml-4"/>
												</tooltip>
											</div>
											<span v-else>-</span>
										</div>
									</template>
								</description-list-item>
								<description-list-item :title="$t('label.time_window')">
									<template #text>
										<dynamic-date-range :start="selectedCampaign.start_date" :end="selectedCampaign.end_date">
										</dynamic-date-range>
									</template>
								</description-list-item>
								<description-list-item v-if="selectedCampaign.gtc" :title="$t('label.participation_gtc')">
									<template #text>
										<btn @click="showGtcModal = true" x-small>
											{{ $t('action.show') }}
										</btn>
									</template>
								</description-list-item>
							</description-list>

							<alert v-if="!$auth.loggedIn || !eventStore.authPermissions.is_participant" :content="$t('text.event.acquisition_campaigns.no_participant')"/>
							<alert v-else-if="selectedCampaign.acquirer" type="success">
								<div class="flex flex-column sm:flex-row items-center justify-between">
									<div v-html="$t('text.event.acquisition_campaigns.registered')"/>
									<btn :to="{path: $web('events.participations.show.campaigns', [eventStore.event._key, selectedCampaign.acquirer.participation_id]), query: {campaign: selectedCampaignKey}}" color="success lighten" class="mt-12 sm:mt-0 sm:ml-12 w-full sm:w-auto">
										<span>{{ $t('action.to_overview') }}</span>
										<icon :icon="icons.next" />
									</btn>
								</div>
							</alert>
							<ajax-form v-else
									   :action="$api('events.acquisition_campaigns.acquirers.store', [eventStore.event._key, selectedCampaignKey])" method="POST" v-model:loading="signUpFormLoading"
									   :data="signUpData"
									   class="mt-16" alert="event.acquisition_campaign.sign_up" :lang-params="{acquisition_campaign: selectedCampaign.name, code: 'coupon.code'}"
									   @success="handleSignUp">

								<alert :content="$t('text.event.acquisition_campaigns.not_registered')"/>

								<form-checkbox v-if="selectedCampaign.gtc" v-model="signUpData.gtc" :rules="{accepted: true}" :disabled="signUpFormLoading"
											   :label="$t('input.participation_gtc')">
									<a href="#" @click.prevent="showGtcModal = true">{{ $t('action.open_participation_gtc') }}</a>
								</form-checkbox>

								<wrapper grow>
									<btn :disabled="signUpFormLoading" :loading="signUpFormLoading" type="submit" color="success">
										<icon :icon="icons.check"/>
										<span>{{ $t('action.register') }}</span>
									</btn>
								</wrapper>
							</ajax-form>

							<modal v-model="showGtcModal" :title="$t('label.participation_gtc')">
								<user-content v-if="selectedCampaign.gtc" :text="selectedCampaign.gtc"/>
								<missing-info v-else :text="$t('missing.event.gtc')" no-spacing/>
							</modal>
						</template>
					</template>
                </card-text>
            </card>
        </column>

        <column cols="12" md="5" lg="4">

        </column>
    </row>
</template>

<script setup lang="ts">
import {EventPageType} from "@spoferan/spoferan-ts-core";
import {toEuro} from "@spoferan/spoferan-ts-core";
import {checkIcon, nextIcon, helpIcon} from "@spoferan/nuxt-spoferan/icons";
import {useEventStore} from "../../../store/event";

const eventStore = useEventStore();
const {t} = useI18n();
const {$api, $web, $auth} = useNuxtApp();
const route = useRoute();
const router = useRouter();
const {updateUrlQuery} = useUrlMutations();

// Make sure the page is activated
if (!eventStore.hasActivePage(EventPageType.AcquisitionCampaigns)) {
	await navigateTo($web('events.show', route.params.event));
}

const {reactiveData: campaigns, pending, error, refresh} = await useApiFetch($auth.loggedIn ? $api('users.event_acquisition_campaigns.index', 'my') : $api('events.acquisition_campaigns.index', eventStore.event.id), {
	params: {
		event_id: eventStore.event.id
	},
	auth: true
});

const initialRouteCampaignKey = route.query.campaign;
const signUpData = ref({});
const showGtcModal = ref(false);
const signUpFormLoading = ref(false);

const icons = {
	check: checkIcon,
	next: nextIcon,
	help: helpIcon,
};

// We only display public and active campaigns or directly shared ones
const selectableCampaigns = computed(() => {
	return campaigns.value ? campaigns.value.filter(campaign => campaign.is_selectable || (initialRouteCampaignKey && campaign._key === initialRouteCampaignKey)) : []
});

const selectedCampaignKey = ref(initialRouteCampaignKey ?? (selectableCampaigns.value.length ? selectableCampaigns.value[0]._key : null));

const selectedCampaign = computed(() => {
	return selectableCampaigns.value.find(campaign => campaign._key === selectedCampaignKey.value);
});

watch(selectableCampaigns, (newValue) => {
	selectedCampaignKey.value = route.query.campaign ? route.query.campaign : (newValue.length ? newValue[0]._key : null);
});

watch(() => $auth.id, () => {
	resetSignUpData();
}, {immediate: true});

watch(selectedCampaignKey, (newKey) => {
	resetSignUpData();
	updateUrlQuery({campaign: newKey ?? undefined})
});

function handleSignUp(data) {
	router.push({path: $web('events.participations.show.campaigns', [eventStore.event._key, data.participation_id]), query: {campaign: selectedCampaignKey.value}})
}

function resetSignUpData() {
	signUpData.value = $auth.id ? {
		user_id: $auth.id
	} : {}
}

useMeta({
	title: eventStore.buildPageTitle(t('view.events.acquisition_campaigns.index.title', {event: eventStore.event.display_name})),
	description: t('view.events.acquisition_campaigns.index.description', {event: eventStore.event.display_name})
})


</script>

